import { FunctionComponent, useContext, useMemo, useState } from 'react'
import { withWidthLowerObserver } from '../../../../misc/WidthObserver.hoc'
import { withTrackingEvent, FwTrackingEvent } from '../../../../misc/Tracking'
import { breakMedium } from '../../../../misc/responsive'

import { PatientsListActionsProps } from './PatientsListActions.model'
import {
  MultiSelectSearch,
  SearchInput,
  SelectSearch,
  TooltipWrapper,
  useSelectSearchFiltering,
} from '../../../shared'
import { ConnectedUserContext } from '../../../../misc/auth.utilities'
import { computeTeamsDoctorsOptions } from '../../../../misc/team.utilities'
import { useDebouncedFilters, useTagSearch } from '../../../../hooks/utils'
import styles from './PatientsListActions.module.scss'
import { inseeNumberValidator } from '../../../../misc/inseeNumber.utils'

const followedPatient = 'followedPatient'

const PatientsListActions: FunctionComponent<PatientsListActionsProps> = ({
  filters: { search, tagIds, userIds, myFollowedPatient, inseeNumber },
  onSearchFiltersChange,
  tags,
  userTeams,
  isLowerThanBreakpoint,
}) => {
  const [debouncedFilters, setDebouncedFilters] = useState({ search, inseeNumber })
  const { currentUser } = useContext(ConnectedUserContext)
  const trackFocusOnSearchField = withTrackingEvent(FwTrackingEvent.SEARCH_PATIENT)(() => {
    // Nothing to do here
  })

  useDebouncedFilters(debouncedFilters, onSearchFiltersChange)

  // Wording conditionel sur desktop
  const placeholder = isLowerThanBreakpoint
    ? 'Rechercher un patient'
    : 'Rechercher un nom ou une date de naissance complète (ex: 17/03/1989)'

  // Options pour le filtrage par médecins
  const doctorSearchOptions = useMemo(
    () =>
      currentUser
        ? [
            {
              value: followedPatient,
              label: 'Mes patients suivis',
            },
            ...computeTeamsDoctorsOptions(userTeams, currentUser, 'Mes patients créés'),
          ]
        : [],
    [userTeams, currentUser],
  )

  const doctorValue = doctorSearchOptions.find(({ value }) =>
    myFollowedPatient ? value === followedPatient : value === userIds[0],
  )
  const [doctorText, setDoctorText, filteredDoctorOptions] = useSelectSearchFiltering(
    doctorValue,
    doctorSearchOptions,
  )

  const [tagOptions, tagValues] = useTagSearch(tagIds, tags)

  const inseeNumberError = useMemo(() => {
    if (!inseeNumber || inseeNumber.length < 1) return null

    const inputLength = inseeNumber.length
    if (inputLength < 13) return 'Matricule INS incomplet'
    if (inputLength === 13) return 'Clé du matricule INS manquante'
    if (inputLength === 14) return 'Clé du matricule INS incomplète'
    if (inputLength > 15) return 'Matricule INS incorrect (trop long)'

    const isValid = inseeNumberValidator(inseeNumber)
    if (!isValid) return 'Matricule INS incorrect'

    return null
  }, [inseeNumber])

  return (
    <div className={styles['searchFields']}>
      <SearchInput
        testId="input-patient-by_name-search"
        autofocus
        value={debouncedFilters.search}
        placeholder={placeholder}
        onChange={({ target: { value } }) =>
          setDebouncedFilters((prev) => ({ ...prev, search: value }))
        }
        onFocus={trackFocusOnSearchField}
      />
      <TooltipWrapper
        content={inseeNumberError}
        display={!!inseeNumberError}
        pointerDirection="top"
        alwaysShow
      >
        <div>
          <SearchInput
            testId="input-patient-by_insee_number-search"
            value={debouncedFilters.inseeNumber}
            placeholder="Rechercher par matricule INS"
            onChange={({ target: { value } }) => {
              // On autorique 'a' et 'b' pour laisser passer les numéros corses
              const sanitizedValue = value.replaceAll(/[^0-9aAbB]/g, '')
              setDebouncedFilters((prev) => ({ ...prev, inseeNumber: sanitizedValue }))
            }}
            icon="hashtag"
            onFocus={trackFocusOnSearchField}
          />
        </div>
      </TooltipWrapper>
      <MultiSelectSearch
        placeholder="Rechercher par label"
        icon="tag"
        testId="multi_select_search-patient-by_tag-search"
        value={tagValues}
        options={tagOptions}
        onSelect={(selected) =>
          onSearchFiltersChange({ tagIds: selected.map(({ value }) => value) })
        }
      />
      {doctorSearchOptions.length > 1 && (
        <SelectSearch
          placeholder="Rechercher par médecin"
          testId="search_input-patient-by_doctor_search"
          icon="profile"
          value={doctorSearchOptions.find(
            (option) => option.value === (myFollowedPatient ? followedPatient : userIds[0]),
          )}
          options={filteredDoctorOptions}
          onSelect={(selected) => {
            onSearchFiltersChange({
              userIds: selected && selected.value !== followedPatient ? [selected.value] : [],
              myFollowedPatient: selected?.value === followedPatient,
            })
          }}
          textValue={doctorText}
          onTextChange={setDoctorText}
          onFocus={() => setDoctorText('')}
        />
      )}
    </div>
  )
}

const withHoc = withWidthLowerObserver(breakMedium)(PatientsListActions)
export { withHoc as PatientsListActions }
